import React from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ScoreIcon from "@mui/icons-material/Score";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: "24px",
  },
  summaryCard: {
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  },
  cardContent: {
    padding: "24px !important",
  },
  headerTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statBox: {
    padding: "16px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    textAlign: "center",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  statIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 8px auto",
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: theme.palette.primary.main,
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  leagueGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    marginTop: "16px",
  },
  leagueItem: {
    padding: "16px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "8px",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
  },
  leagueName: {
    fontWeight: 600,
    fontSize: "0.875rem",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  leagueStats: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  divisionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "8px",
    marginTop: "16px",
  },
  divisionBox: {
    padding: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: "6px",
    textAlign: "center",
  },
  divisionNumber: {
    fontWeight: 600,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  divisionMultiplier: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.primary.main,
  },
  botXpGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "8px",
    marginTop: "16px",
  },
  botXpBox: {
    padding: "12px 8px",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: "6px",
    textAlign: "center",
  },
  botLeagueName: {
    fontWeight: 600,
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    marginBottom: "4px",
  },
  botXpValue: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#7b1fa2",
  },
}));

const LEAGUE_COLORS = {
  STARTER: "#4caf50",
  BRONZE: "#8d6e63",
  BRONZE_II: "#795548",
  SILVER: "#9e9e9e",
  SILVER_II: "#607d8b",
  GOLD: "#ffc107",
  GOLD_II: "#ff9800",
  DIAMOND: "#2196f3",
  DIAMOND_II: "#3f51b5",
  PLATINUM: "#9c27b0",
};

const ConfigurationSummaryCards = ({ config, rewardPackOptions, onEdit }: any) => {
  const styles = useStyles();

  const getConfiguredLeaguesCount = () => {
    if (!Array.isArray(config.leagueConfig)) return 0;

    return config.leagueConfig.filter((leagueItem) => {
      const leagueData = leagueItem.leagueConfig;
      return (
        leagueData.rewards &&
        leagueData.rewards.length > 0 &&
        leagueData.rewards.some((reward) => reward.rewardPack)
      );
    }).length;
  };

  const getAverageMultiplier = () => {
    const multipliers = Object.values(config.divisionMultipliers || {});
    const sum = multipliers.reduce((acc: number, val: any) => acc + (Number(val) || 0), 0);
    return multipliers.length > 0
      ? ((sum as number) / multipliers.length).toFixed(2)
      : "0.00";
  };

  const getRewardPackNames = (rewards: any) => {
    if (!rewards || !Array.isArray(rewards)) return [];

    return rewards
      .filter((reward) => reward.rewardPack)
      .map((reward) => {
        const pack = rewardPackOptions.find((p) => p.id === reward.rewardPack);
        const packName = pack ? pack.label.split(" - ")[0] : "Unknown";
        return `${packName} (×${reward.rewardPackCount || 1})`;
      });
  };

  return (
    <Box className={styles.container}>
      <Card className={styles.summaryCard}>
        <CardContent className={styles.cardContent}>
          {/* Basic Configuration */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            ⚙️ Basic Configuration
          </Typography>

          {/* Main Stats */}
          <Grid container spacing={3}>
            <Grid item xs={6} sm={4}>
              <Box className={styles.statBox}>
                <Box
                  className={styles.statIcon}
                  sx={{ backgroundColor: "#e8f5e8", color: "#2e7d32" }}
                >
                  <EmojiEventsIcon fontSize="small" />
                </Box>
                <Typography className={styles.statValue}>
                  {config.matchWinXP || 0}
                </Typography>
                <Typography className={styles.statLabel}>
                  Base XP per Win
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Box className={styles.statBox}>
                <Box
                  className={styles.statIcon}
                  sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
                >
                  <ScoreIcon fontSize="small" />
                </Box>
                <Typography className={styles.statValue}>
                  {config.vaultXPMultiplier || 0}x
                </Typography>
                <Typography className={styles.statLabel}>
                  Vault Multiplier
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box className={styles.statBox}>
                <Box
                  className={styles.statIcon}
                  sx={{ backgroundColor: "#fff3e0", color: "#e65100" }}
                >
                  <AccessTimeIcon fontSize="small" />
                </Box>
                <Typography className={styles.statValue}>
                  {config.activeUserTimeMin > 0 ? `${config.activeUserTimeMin}m` : "Default"}
                </Typography>
                <Typography className={styles.statLabel}>
                  Active User Window
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Bot Base XP Summary */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            🤖 Bot Base XP
          </Typography>
          <Box className={styles.botXpGrid}>
            {config.botBaseXp && Object.entries(config.botBaseXp)
              .map(([league, xp]) => (
                <Box key={league} className={styles.botXpBox}>
                  <Typography className={styles.botLeagueName}>
                    {league.replace(/_/g, " ")}
                  </Typography>
                  <Typography className={styles.botXpValue}>
                    {xp} XP
                  </Typography>
                </Box>
              ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Division Multipliers Summary */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            🎯 Division Multipliers (Avg: {getAverageMultiplier()}x)
          </Typography>
          <Box className={styles.divisionGrid}>
            {Object.entries(config.divisionMultipliers || {})
              .sort(([a], [b]) => parseInt(a) - parseInt(b))
              .map(([division, multiplier]) => (
                <Box key={division} className={styles.divisionBox}>
                  <Typography className={styles.divisionNumber}>
                    Div {division}
                  </Typography>
                  <Typography className={styles.divisionMultiplier}>
                    {multiplier}x
                  </Typography>
                </Box>
              ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* League Configuration Summary */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            🏆 League Configuration ({getConfiguredLeaguesCount()}/10 Leagues
            Configured)
          </Typography>
          <Box className={styles.leagueGrid}>
            {Array.isArray(config.leagueConfig)
              ? config.leagueConfig
                  .filter((leagueItem) => {
                    const league = leagueItem.leagueConfig;
                    return (
                      league.rewards &&
                      league.rewards.length > 0 &&
                      league.rewards.some((reward) => reward.rewardPack)
                    );
                  })
                  .map((leagueItem) => {
                    const leagueName = leagueItem.name;
                    const league = leagueItem.leagueConfig;
                    const rewardPackNames = getRewardPackNames(league.rewards);

                    return (
                      <Box key={leagueName} className={styles.leagueItem}>
                        <Typography className={styles.leagueName}>
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              backgroundColor:
                                LEAGUE_COLORS[leagueName] || "#666",
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                            {leagueName.replace(/_/g, " ")}
                          </Box>
                        </Typography>
                        <Box className={styles.leagueStats}>
                          {league.promotionPositions?.length > 0 && (
                            <Chip
                              label={`↑ Top ${league.promotionPositions.length} Promo`}
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                height: "22px",
                                fontWeight: 600,
                                backgroundColor: "#e8f5e8",
                                color: "#2e7d32",
                                border: "1px solid #4caf50",
                              }}
                            />
                          )}
                          {league.demotionPositions?.length > 0 && (
                            <Chip
                              label={`↓ Bottom ${league.demotionPositions.length} Demo`}
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                height: "22px",
                                fontWeight: 600,
                                backgroundColor: "#ffebee",
                                color: "#c62828",
                                border: "1px solid #f44336",
                              }}
                            />
                          )}
                          {rewardPackNames.map((rewardName, index) => (
                            <Chip
                              key={index}
                              label={rewardName}
                              size="small"
                              sx={{
                                fontSize: "0.7rem",
                                height: "22px",
                                fontWeight: 600,
                                backgroundColor: "#f3e5f5",
                                color: "#7b1fa2",
                                border: "1px solid #9c27b0",
                                marginTop: index > 0 ? "4px" : 0,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })
              : []}
          </Box>

          {getConfiguredLeaguesCount() === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontStyle: "italic", mt: 1 }}
            >
              No leagues configured yet. Configure leagues to see summary here.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConfigurationSummaryCards;
